import React, {
  Children,
  useImperativeHandle,
  ForwardRefRenderFunction,
  PropsWithChildren,
  useCallback,
  useEffect,
  useState,
  useRef,
  forwardRef,
  ForwardedRef,
} from 'react';
import _range from 'lodash/range';
import _min from 'lodash/min';
import _max from 'lodash/max';
import cls from 'classnames';

import './style.scss';

export const parseToNumber = (val: string) => {
  return Number(val.replace('px', ''));
};

interface MasonryProps extends PropsWithChildren<unknown> {
  className?: string;
  columnCount: number;
}

export interface MasonryRef {
  postLayout: (func: () => void) => void;
}

function useContainerRef<T = HTMLDivElement>() {
  const [ref, setCurrentRef] = useState<T>();

  const setRef = useCallback((instance: T) => {
    if (instance) {
      setCurrentRef(instance);
    }
  }, []);

  return [ref, setRef] as [T, React.RefCallback<T>];
}

export const Masonry = forwardRef((props: MasonryProps, ref: ForwardedRef<MasonryRef>) => {
  const { children, columnCount, className } = props;
  const childrenArray = Children.toArray(children);
  const [container, setContainer] = useContainerRef<HTMLDivElement>();
  const [containerHeight, setContainerHeight] = useState(
    `calc(${Math.floor(childrenArray.length / columnCount + 1)} * var(--masonry-item-default-height))`,
  );
  const postLayoutQueue = useRef<(() => void)[]>([]);

  useImperativeHandle(ref, () => {
    return {
      postLayout: (func) => {
        postLayoutQueue.current.push(func);
      },
    };
  });

  const [heightList, setHeightList] = useState(_range(childrenArray.length).map(() => 0));

  const [orderList, setOrderList] = useState(_range(childrenArray.length).map((o) => (o % columnCount) + 1));

  const doLayout = useCallback(() => {
    if (!container) {
      return;
    }

    const columnHeights: number[] = new Array(columnCount).fill(0);
    const columnItems: number[] = new Array(columnCount).fill(0);
    const childOrderList = [];
    for (let i = 0; i < container.children.length; i++) {
      const child = container.children.item(i);
      if (!child) {
        return;
      }
      const min = _min(columnHeights);
      const minIndex = columnHeights.findIndex((v) => v === min);
      childOrderList.push(minIndex + 1);
      const style = window.getComputedStyle(child);
      columnHeights[minIndex] += parseToNumber(style.height);
      columnItems[minIndex] += 1;
    }

    setHeightList(columnHeights);
    setOrderList(childOrderList);

    setContainerHeight(
      `max(${columnHeights
        .map(
          (_, index) =>
            `${columnHeights[index]}px + ${Math.max(
              columnItems[index] - 1,
              0,
            )} * var(--masonry-items-horizontal-interval) + 2 * var(--masonry-padding-vertical)`,
        )
        .join(',')})`,
    );

    if (postLayoutQueue.current.length) {
      postLayoutQueue.current.forEach((func) => func());
      postLayoutQueue.current = [];
    }
  }, [container, columnCount]);

  useEffect(() => {
    if (!container) {
      return;
    }

    requestAnimationFrame(() => {
      doLayout();
    });

    const observer = new ResizeObserver(doLayout);
    observer.observe(container);

    for (let i = 0; i < container.children.length; i++) {
      const child = container.children.item(i);
      if (child) {
        observer.observe(child);
      }
    }

    return () => {
      observer.disconnect();
    };
  }, [container, doLayout, children]);

  const maxHeight = _max(heightList) || 0;
  const maxHeightIndex = heightList.indexOf(maxHeight) - 1;
  const maxHeightCount = orderList.filter((item) => item === maxHeightIndex + 1).length;

  return (
    <div
      className={cls('lemon8-masonry', className)}
      style={
        {
          '--masonry-column-count': columnCount,
          height: containerHeight,
        } as any
      }
      ref={setContainer}
    >
      {childrenArray.map((child, index) => (
        <div
          key={(child as any).key}
          className={cls('lemon8-masonry-item', {
            left: orderList[index] === 1,
            top: orderList.findIndex((r) => r === orderList[index]) === index,
            bottom: orderList.lastIndexOf(orderList[index]) === index,
          })}
          style={{
            order: orderList[index] || columnCount + 1,
            marginBottom:
              orderList.lastIndexOf(orderList[index]) === index
                ? `calc(${
                    (maxHeight || 0) - heightList[orderList[index] - 1]
                  }px + var(--masonry-padding-horizontal) * (${
                    orderList.filter((item) => item === index + 1).length - maxHeightCount
                  }))`
                : 0,
          }}
        >
          {child}
        </div>
      ))}
    </div>
  );
});
